import {
  PortfolioIngredientSearch,
  PortfolioIngredientSearchPage,
} from '@/services/portfolioIngredientSearch';
import { request } from '@umijs/max';

export const searchGroupedPortfolioIngredients: PortfolioIngredientSearch = ({
  signal,
  ...params
}) =>
  request<PortfolioIngredientSearchPage>('/data/portfolio/ingredient-search', {
    params,
    signal,
    skipErrorHandler: true,
  });

/**
 * Fetch current user profile information
 */
export async function fetchCurrentUser() {
  return request('/user/api/currentUser', {
    method: 'GET',
  });
}

/**
 * Fetch current user's subscription and credits information
 */
export async function fetchCurrentPlan() {
  return request('/user/current-plan', {
    method: 'GET',
  });
}

/**
 * Update user profile information
 */
export async function updateUserProfile(params: { fullName: string; phoneNumber: string }) {
  return request('/user/update-profile', {
    method: 'POST',
    data: params,
  });
}

export async function updateIngredientsOfInterest(params: {
  ingredients: Record<string, unknown>[];
}) {
  return request('/user/ingredients-of-interest', {
    method: 'POST',
    data: params,
  });
}

export async function updateIngredientsToReplace(params: {
  ingredients: Record<string, unknown>[];
}) {
  return request('/user/ingredients-to-replace', {
    method: 'POST',
    data: params,
  });
}

export async function fetchIngredientsOfInterest() {
  return request('/user/ingredients-of-interest', {
    method: 'GET',
  });
}

export async function fetchIngredientInterestGroups() {
  return request('/user/ingredient-interest-groups', {
    method: 'GET',
  });
}

export type IngredientInterestGroupPayload = {
  name: string;
  ingredient_ids: number[];
};

export async function createIngredientInterestGroup(params: IngredientInterestGroupPayload) {
  return request('/user/ingredient-interest-groups', {
    method: 'POST',
    data: params,
  });
}

export async function updateIngredientInterestGroup(
  groupId: number,
  params: IngredientInterestGroupPayload,
) {
  return request(`/user/ingredient-interest-groups/${groupId}`, {
    method: 'PUT',
    data: params,
  });
}

export async function deleteIngredientInterestGroup(groupId: number) {
  return request(`/user/ingredient-interest-groups/${groupId}`, {
    method: 'DELETE',
  });
}

export async function fetchIngredientsToReplace() {
  return request('/user/ingredients-to-replace', {
    method: 'GET',
  });
}

/** Remove one or more ingredients of interest, or pass clearAll to empty the list. */
export async function removeIngredientsOfInterest(params: {
  name?: string;
  names?: string[];
  clearAll?: boolean;
}) {
  return request('/user/ingredients-of-interest', {
    method: 'DELETE',
    data: params,
  });
}

/** Remove one or more ingredients to replace, or pass clearAll to empty the list. */
export async function removeIngredientsToReplace(params: {
  name?: string;
  names?: string[];
  clearAll?: boolean;
}) {
  return request('/user/ingredients-to-replace', {
    method: 'DELETE',
    data: params,
  });
}

/**
 * Change user password
 */
export async function changePassword(params: { currentPassword: string; newPassword: string }) {
  return request('/user/change-password', {
    method: 'PUT',
    data: params,
  });
}

export async function exportUsageReport(params: { from: string; to: string }): Promise<Blob> {
  return request('/user/usage/export', {
    method: 'GET',
    params,
    responseType: 'blob',
  });
}

/**
 * Fetch excluded marketers list
 */
export async function fetchExcludedMarketers() {
  return request('/data/get_excluded_marketers', {
    method: 'GET',
  });
}

/**
 * Update excluded marketers list
 */
export async function addExcludedMarketers(params: { marketer_ids: number[] }) {
  return request('/data/add_excluded_marketers', {
    method: 'POST',
    data: params,
  });
}

export async function removeExcludedMarketers(params: { marketer_id: number }) {
  return request('/data/remove_excluded_marketers', {
    method: 'DELETE',
    data: params,
  });
}

/**
 * Fetch excluded manufacturers list
 */
export async function fetchExcludedManufacturers() {
  return request('/data/get_excluded_manufacturers', {
    method: 'GET',
  });
}

/**
 * Update excluded manufacturers list
 */
export async function addExcludedManufacturers(params: { manufacturer_ids: number[] }) {
  return request('/data/add_excluded_manufacturers', {
    method: 'POST',
    data: params,
  });
}

export async function removeExcludedManufacturers(params: { manufacturer_id: number }) {
  return request('/data/remove_excluded_manufacturers', {
    method: 'DELETE',
    data: params,
  });
}

export async function fetchMarketersOfInterest() {
  return request('/data/get_marketers_of_interest', {
    method: 'GET',
  });
}

export async function addMarketersOfInterest(params: { marketer_ids: number[] }) {
  return request('/data/add_marketers_of_interest', {
    method: 'POST',
    data: params,
  });
}

export async function removeMarketerOfInterest(params: { marketer_id: number }) {
  return request('/data/remove_marketer_of_interest', {
    method: 'DELETE',
    data: params,
  });
}

export async function clearMarketersOfInterest() {
  return request('/data/clear_marketers_of_interest', {
    method: 'DELETE',
  });
}

export async function fetchManufacturersOfInterest() {
  return request('/data/get_manufacturers_of_interest', {
    method: 'GET',
  });
}

export async function addManufacturersOfInterest(params: { manufacturer_ids: number[] }) {
  return request('/data/add_manufacturers_of_interest', {
    method: 'POST',
    data: params,
  });
}

export async function removeManufacturerOfInterest(params: { manufacturer_id: number }) {
  return request('/data/remove_manufacturer_of_interest', {
    method: 'DELETE',
    data: params,
  });
}

export async function clearManufacturersOfInterest() {
  return request('/data/clear_manufacturers_of_interest', {
    method: 'DELETE',
  });
}

export async function fetchBrandsOfInterest() {
  return request('/data/get_brands_of_interest', {
    method: 'GET',
  });
}

export async function addBrandsOfInterest(params: { brand_ids: number[] }) {
  return request('/data/add_brands_of_interest', {
    method: 'POST',
    data: params,
  });
}

export async function removeBrandOfInterest(params: { brand_id: number }) {
  return request('/data/remove_brand_of_interest', {
    method: 'DELETE',
    data: params,
  });
}

export async function clearBrandsOfInterest() {
  return request('/data/clear_brands_of_interest', {
    method: 'DELETE',
  });
}

export async function fetchPortfolioScopeInterests() {
  return request('/data/portfolio_scope_interests', {
    method: 'GET',
  });
}

export async function updatePortfolioScopeInterests(params: {
  application_ids: number[];
  countries_of_origin: string[];
  countries_of_sale: string[];
}) {
  return request('/data/portfolio_scope_interests', {
    method: 'PUT',
    data: params,
  });
}

export async function fetchPortfolioProductInterests() {
  return request('/data/portfolio_product_interests', {
    method: 'GET',
  });
}

export async function updatePortfolioProductInterests(params: {
  flavour_ids?: number[];
  fragrance_ids?: number[];
  packaging_types?: string[];
  packaging_material_ids?: number[];
}) {
  return request('/data/portfolio_product_interests', {
    method: 'PATCH',
    data: params,
  });
}
